import { createContext, useContext, ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import { useLevel } from '../contexts/LevelContext';
import LoadingOverlay from './LoadingOverlay';

interface AppLoadingContextType {
  isLoading: boolean;
}

const AppLoadingContext = createContext<AppLoadingContextType | undefined>(undefined);

export const useAppLoading = () => {
  const context = useContext(AppLoadingContext);
  if (context === undefined) {
    throw new Error('useAppLoading must be used within AppLoadingProvider');
  }
  return context;
};

interface AppLoadingProviderProps {
  children: ReactNode;
}

export const AppLoadingProvider: React.FC<AppLoadingProviderProps> = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const { loading: walletLoading } = useWallet();
  const { loading: levelLoading } = useLevel();

  // Show loading if:
  // 1. Auth is still loading
  // 2. User is logged in AND (wallet is loading OR level is loading)
  const isLoading = authLoading || (currentUser && (walletLoading || levelLoading));

  // Determine loading message based on what's loading
  const getLoadingMessage = () => {
    if (authLoading) return 'Authenticating...';
    if (walletLoading) return 'Loading wallet...';
    if (levelLoading) return 'Loading level...';
    return 'Loading...';
  };

  return (
    <AppLoadingContext.Provider value={{ isLoading }}>
      {children}
      <LoadingOverlay show={isLoading} message={getLoadingMessage()} />
    </AppLoadingContext.Provider>
  );
};

